import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { 
  Terminal,
  Server,
  Check,
  Copy,
  ExternalLink,
  Apple,
  Monitor,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const installCommands = [
  {
    os: 'macOS / Linux',
    icon: Apple,
    command: 'curl -fsSL https://ollama.com/install.sh | sh',
    note: 'One-line install script'
  },
  {
    os: 'Windows',
    icon: Monitor,
    command: 'Download from ollama.com/download',
    note: 'Download the installer',
    isLink: true,
    url: 'https://ollama.com/download'
  },
  {
    os: 'Docker',
    icon: Server,
    command: 'docker run -d -p 11434:11434 ollama/ollama',
    note: 'Run in container'
  }
];

const steps = [
  {
    title: 'Install Ollama',
    description: 'Choose your platform and run the install command'
  },
  {
    title: 'Pull a Model',
    description: 'Download an LLM model to use',
    command: 'ollama pull llama3'
  },
  {
    title: 'Pull Embeddings Model (Optional)',
    description: 'For local embeddings',
    command: 'ollama pull nomic-embed-text'
  },
  {
    title: 'Verify Installation',
    description: 'Check that Ollama is running',
    command: 'curl http://localhost:11434/api/tags'
  },
  {
    title: 'Configure VaultAI',
    description: 'Enter your Ollama URL in Settings',
    url: 'http://your-server-ip:11434'
  }
];

export default function OllamaGuidePage() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="py-8 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium">
              <Server className="w-4 h-4" />
              Setup Guide
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              Install Ollama for Private AI
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Run AI models locally for complete data privacy. No data leaves your infrastructure.
            </p>
          </div>

          {/* Install Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                Step 1: Install Ollama
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {installCommands.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={i}
                    className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{item.os}</p>
                      <p className="text-sm text-muted-foreground">{item.note}</p>
                    </div>
                    {item.isLink ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="gap-2">
                          <ExternalLink className="w-4 h-4" />
                          Download
                        </Button>
                      </a>
                    ) : (
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-background px-3 py-2 rounded border border-border font-mono">
                          {item.command}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(item.command)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Steps */}
          <div className="space-y-4">
            {steps.slice(1).map((step, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary">{i + 2}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{step.description}</p>
                      {step.command && (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm bg-secondary px-4 py-3 rounded-lg border border-border font-mono text-foreground">
                            {step.command}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(step.command)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {step.url && (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm bg-secondary px-4 py-3 rounded-lg border border-border font-mono text-primary">
                            {step.url}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(step.url)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Available Models */}
          <Card>
            <CardHeader>
              <CardTitle>Recommended Models</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: 'llama3', desc: 'Meta\'s Llama 3 - Great general purpose', size: '4.7 GB' },
                  { name: 'llama3:70b', desc: 'Llama 3 70B - More capable, needs more RAM', size: '40 GB' },
                  { name: 'mistral', desc: 'Mistral 7B - Fast and efficient', size: '4.1 GB' },
                  { name: 'mixtral', desc: 'Mixtral 8x7B - Mixture of experts', size: '26 GB' },
                  { name: 'nomic-embed-text', desc: 'Embeddings model for search', size: '274 MB' },
                  { name: 'mxbai-embed-large', desc: 'Large embeddings model', size: '670 MB' }
                ].map((model, i) => (
                  <div 
                    key={i}
                    className="p-4 rounded-lg bg-secondary/50 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-mono text-foreground">{model.name}</p>
                      <p className="text-sm text-muted-foreground">{model.desc}</p>
                    </div>
                    <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded">
                      {model.size}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Success Checklist */}
          <Card className="border-emerald-500/30">
            <CardHeader>
              <CardTitle className="text-emerald-400 flex items-center gap-2">
                <Check className="w-5 h-5" />
                You're Ready When...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {[
                  'Ollama is running on your server',
                  'You can access http://your-server:11434 from VaultAI',
                  'At least one LLM model is pulled (e.g., llama3)',
                  'An embeddings model is pulled (e.g., nomic-embed-text)',
                  'Ollama URL is configured in VaultAI Settings'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-emerald-500/50 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              Once Ollama is running, configure it in your VaultAI settings
            </p>
            <div className="flex items-center justify-center gap-4">
              <a href="https://ollama.com/library" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Browse Models
                </Button>
              </a>
              <Link to="/dashboard/settings">
                <Button className="btn-glow gap-2">
                  <Settings className="w-4 h-4" />
                  Go to Settings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
